import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload, Plus, X, Download } from "lucide-react";
import { entities, uploadFile, invokeLLM, getDisplayMessage, openUploadInNewTab, downloadUploadFile } from "@/api/apiClient";
import { useAuth } from "@/lib/AuthContext";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { useQuery } from "@tanstack/react-query";

const TIPOS_CATALOGO_FALLBACK = [
  { codigo: "cnd_federal", nome: "Certidão Negativa de Débitos Federais (CND Federal)", modoControle: "auto_renovavel", sugestaoCategoria: "Certidões" },
  { codigo: "divida_ativa_uniao", nome: "Certidão Dívida Ativa da União", modoControle: "auto_renovavel", sugestaoCategoria: "Certidões" },
  { codigo: "conjunta_rfb_pgfn", nome: "Certidão Conjunta Receita Federal + PGFN", modoControle: "auto_renovavel", sugestaoCategoria: "Certidões" },
  { codigo: "cndt", nome: "CNDT — Certidão Negativa de Débitos Trabalhistas", modoControle: "auto_renovavel", sugestaoCategoria: "Certidões" },
  { codigo: "crf_fgts", nome: "CRF FGTS — Certificado de Regularidade do FGTS", modoControle: "auto_renovavel", sugestaoCategoria: "Certidões" },
  { codigo: "certidao_estadual_debitos", nome: "Certidão Estadual de Débitos Tributários", modoControle: "auto_renovavel", sugestaoCategoria: "Certidões" },
  { codigo: "inscricao_estadual", nome: "Inscrição Estadual", modoControle: "manual_controlado", sugestaoCategoria: "Empresa" },
  { codigo: "certidao_municipal", nome: "Certidão Municipal", modoControle: "auto_renovavel", sugestaoCategoria: "Certidões" },
  { codigo: "inscricao_municipal", nome: "Inscrição Municipal", modoControle: "manual_controlado", sugestaoCategoria: "Empresa" },
  { codigo: "alvara_funcionamento", nome: "Alvará de Funcionamento", modoControle: "manual_controlado", sugestaoCategoria: "Empresa" },
  { codigo: "sicaf", nome: "SICAF", modoControle: "auto_renovavel", sugestaoCategoria: "Certidões" },
  { codigo: "compras_gov", nome: "Compras.gov", modoControle: "auto_renovavel", sugestaoCategoria: "Certidões" },
  { codigo: "cadastro_estadual_fornecedores", nome: "Cadastro estadual de fornecedores", modoControle: "manual_controlado", sugestaoCategoria: "Certidões" },
  { codigo: "cadastro_municipal_fornecedores", nome: "Cadastro municipal de fornecedores", modoControle: "manual_controlado", sugestaoCategoria: "Certidões" },
  { codigo: "balanco_patrimonial", nome: "Balanço Patrimonial", modoControle: "manual_controlado", sugestaoCategoria: "Outros" },
  { codigo: "dre", nome: "DRE — Demonstração do Resultado", modoControle: "manual_controlado", sugestaoCategoria: "Outros" },
  { codigo: "indices_financeiros", nome: "Índices Financeiros (LG, LC, SG)", modoControle: "manual_controlado", sugestaoCategoria: "Outros" },
  { codigo: "falencia_concordata", nome: "Certidão de Falência e Concordata", modoControle: "manual_controlado", sugestaoCategoria: "Certidões" },
  { codigo: "recuperacao_judicial", nome: "Recuperação Judicial", modoControle: "manual_controlado", sugestaoCategoria: "Certidões" },
  { codigo: "patrimonio_liquido_minimo", nome: "Patrimônio Líquido mínimo", modoControle: "manual_controlado", sugestaoCategoria: "Outros" },
  { codigo: "capital_social_minimo", nome: "Capital Social mínimo", modoControle: "manual_controlado", sugestaoCategoria: "Outros" },
];

function normalizeTipoCatalogoItem(item) {
  if (!item || typeof item !== "object") return null;
  const codigo = item.codigo ?? item.id ?? item.value ?? "";
  if (!codigo) return null;
  return {
    codigo,
    nome: item.nome ?? item.label ?? codigo,
    modoControle: item.modoControle ?? item.modo_controle ?? "manual_controlado",
    sugestaoCategoria: item.sugestaoCategoria ?? item.sugestao_categoria ?? "",
  };
}

export default function DocumentoForm({ documento, onSalvar, onCancelar, isSaving }) {
  const [formData, setFormData] = useState(documento || {
    documento_tipo_id: "",
    descricao: "",
    tipo: "",
    categoria: "",
    modo_controle: "manual_controlado",
    tipo_validade: "data_fixa",
    competencia_mes_ano: "",
    criterio_textual: "",
    valor_declarado: "",
    valor_minimo_exigido: "",
    status_conformidade: "pendente_analise",
    status_operacional: "pendente_renovacao",
    data_emissao: "",
    data_validade_inicial: "",
    data_validade_final: "",
    link_solicitacao: "",
    anexo_url: "",
    anexo_nome: "",
    historico_anexos: [],
    conteudo_extraido: "",
    emails_notificacao: [],
    observacoes: "",
    status: "valido"
  });

  const [uploading, setUploading] = useState(false);
  const [uploadingInicial, setUploadingInicial] = useState(false);
  const [novoEmail, setNovoEmail] = useState("");
  const [extractingContent, setExtractingContent] = useState(false);
  const [novaDataValidade, setNovaDataValidade] = useState("");
  const { user } = useAuth();
  const isEdicao = !!documento;
  const { data: tiposCatalogo = TIPOS_CATALOGO_FALLBACK } = useQuery({
    queryKey: ["documentos", "tipos-catalogo"],
    queryFn: async () => {
      try {
        const remote = await entities.Documento.tiposCatalogo();
        if (!Array.isArray(remote) || remote.length === 0) return TIPOS_CATALOGO_FALLBACK;
        const normalized = remote
          .map(normalizeTipoCatalogoItem)
          .filter(Boolean);
        return normalized.length > 0 ? normalized : TIPOS_CATALOGO_FALLBACK;
      } catch {
        return TIPOS_CATALOGO_FALLBACK;
      }
    },
  });

  const handleUploadAnexoInicial = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingInicial(true);
    try {
      const { file_url } = await uploadFile(file);
      
      setFormData({
        ...formData,
        anexo_url: file_url,
        anexo_nome: file.name
      });

      await extrairConteudo(file_url);
    } catch (error) {
      console.error("Erro ao fazer upload:", error);
      toast.error(getDisplayMessage(error, "Erro ao fazer upload do arquivo. Tente novamente."));
    } finally {
      setUploadingInicial(false);
    }
  };

  const handleUploadAnexo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!novaDataValidade) {
      alert("Por favor, informe a data de validade final do novo documento");
      e.target.value = '';
      return;
    }

    setUploading(true);
    try {
      const { file_url } = await uploadFile(file);
      
      // Mover o anexo atual para o histórico se existir
      const historicoAtualizado = [...(formData.historico_anexos || [])];
      if (formData.anexo_url) {
        historicoAtualizado.push({
          url: formData.anexo_url,
          nome: formData.anexo_nome,
          data_adicao: new Date().toISOString(),
          data_validade_final: formData.data_validade_final
        });
      }

      setFormData({
        ...formData,
        anexo_url: file_url,
        anexo_nome: file.name,
        data_validade_final: novaDataValidade,
        historico_anexos: historicoAtualizado
      });

      setNovaDataValidade("");
      e.target.value = '';

      await extrairConteudo(file_url);
      
      alert(`✅ Documento renovado com sucesso!\n\n${formData.anexo_nome ? `O documento anterior "${formData.anexo_nome}" foi movido para o histórico.` : ''}\n\nNovo documento: ${file.name}\nNova validade: ${new Date(novaDataValidade).toLocaleDateString('pt-BR')}`);
    } catch (error) {
      console.error("Erro ao fazer upload:", error);
      toast.error(getDisplayMessage(error, "Erro ao fazer upload do arquivo. Tente novamente."));
      e.target.value = '';
    } finally {
      setUploading(false);
    }
  };

  const extrairConteudo = async (file_url) => {
    setExtractingContent(true);
    try {
      // Tentar extrair texto do documento
      const resultado = await invokeLLM({
        prompt: "Extraia TODO o texto deste documento. Retorne apenas o texto extraído, sem comentários ou análises.",
        file_urls: [file_url]
      });

      if (resultado) {
        setFormData(prev => ({
          ...prev,
          conteudo_extraido: typeof resultado === 'string' ? resultado : JSON.stringify(resultado)
        }));
      }
    } catch (error) {
      console.error("Erro ao extrair conteúdo:", error);
      const msg = (error?.message || error?.data?.message || "").toLowerCase();
      if (msg.includes("pdf muito grande") || (msg.includes("máximo") && msg.includes("páginas"))) {
        toast.error("O PDF excede o limite de 500 páginas. Envie um arquivo com no máximo 500 páginas.");
      } else {
        toast.error(getDisplayMessage(error, "Erro ao extrair conteúdo. Tente novamente."));
      }
    } finally {
      setExtractingContent(false);
    }
  };

  const handleAddEmail = () => {
    if (!novoEmail.trim() || !novoEmail.includes('@')) {
      alert("Digite um e-mail válido");
      return;
    }

    const emails = formData.emails_notificacao || [];
    if (emails.includes(novoEmail.trim())) {
      alert("Este e-mail já foi adicionado");
      return;
    }

    setFormData({
      ...formData,
      emails_notificacao: [...emails, novoEmail.trim()]
    });
    setNovoEmail("");
  };

  const handleRemoveEmail = (email) => {
    const lista = formData.emails_notificacao || [];
    setFormData({
      ...formData,
      emails_notificacao: lista.filter((e) => e !== email)
    });
  };

  const handleRemoveHistoricoAnexo = (index) => {
    const historicoAtualizado = formData.historico_anexos.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      historico_anexos: historicoAtualizado
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.descricao) {
      alert("Preencha o campo Descrição");
      return;
    }
    if (formData.tipo_validade === "data_fixa" && !formData.data_validade_final) {
      alert("Preencha o campo Validade Final");
      return;
    }
    onSalvar({
      ...formData,
      valor_declarado:
        formData.valor_declarado === "" || formData.valor_declarado == null
          ? null
          : Number(formData.valor_declarado),
      valor_minimo_exigido:
        formData.valor_minimo_exigido === "" || formData.valor_minimo_exigido == null
          ? null
          : Number(formData.valor_minimo_exigido),
      emails_notificacao: Array.isArray(formData.emails_notificacao)
        ? formData.emails_notificacao
        : []
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid md:grid-cols-2 gap-4">
        <div className="space-y-2 md:col-span-2">
          <Label htmlFor="descricao">Descrição da Certidão/Documento *</Label>
          <Input
            id="descricao"
            value={formData.descricao}
            onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
            placeholder="Ex: Certidão Negativa de Débitos Federais"
            required
          />
        </div>

        <div className="space-y-2 md:col-span-2">
          <Label htmlFor="documento_tipo_id">Tipo oficial (catálogo)</Label>
          <Select
            value={formData.documento_tipo_id || "none"}
            onValueChange={(value) => {
              const selected = tiposCatalogo.find((item) => item.codigo === value);
              if (!selected) {
                setFormData({
                  ...formData,
                  documento_tipo_id: "",
                });
                return;
              }
              setFormData({
                ...formData,
                documento_tipo_id: selected.codigo,
                descricao: formData.descricao || selected.nome,
                categoria: formData.categoria || selected.sugestaoCategoria || "",
                modo_controle: selected.modoControle || formData.modo_controle,
                status_operacional:
                  selected.modoControle === "auto_renovavel" ? "pendente_renovacao" : "pendente_analise",
              });
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="Selecionar do catálogo de regularidade/cadastros/financeiro" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Não vincular ao catálogo</SelectItem>
              {tiposCatalogo.map((item) => (
                <SelectItem key={item.codigo} value={item.codigo}>
                  {item.nome}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-slate-500">
            Use o catálogo para classificar obrigações de regularidade fiscal, trabalhista, cadastral e financeira.
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="tipo">Tipo de Documento</Label>
          <Select
            value={formData.tipo}
            onValueChange={(value) => setFormData({ ...formData, tipo: value })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Selecione o tipo" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Certidão">Certidão</SelectItem>
              <SelectItem value="Contrato Social">Contrato Social</SelectItem>
              <SelectItem value="Procuração">Procuração</SelectItem>
              <SelectItem value="Alvará">Alvará</SelectItem>
              <SelectItem value="Licença">Licença</SelectItem>
              <SelectItem value="CNPJ">CNPJ</SelectItem>
              <SelectItem value="Inscrição Estadual">Inscrição Estadual</SelectItem>
              <SelectItem value="Outros">Outros</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="categoria">Categoria</Label>
          <Select
            value={formData.categoria}
            onValueChange={(value) => setFormData({ ...formData, categoria: value })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Selecione a categoria" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Empresa">Empresa</SelectItem>
              <SelectItem value="Sócio">Sócio</SelectItem>
              <SelectItem value="Certidões">Certidões</SelectItem>
              <SelectItem value="Atestados de Capacidade">Atestados de Capacidade</SelectItem>
              <SelectItem value="Certificados">Certificados</SelectItem>
              <SelectItem value="Outros">Outros</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="data_emissao">Data de Emissão</Label>
          <Input
            id="data_emissao"
            type="date"
            value={formData.data_emissao}
            onChange={(e) => setFormData({ ...formData, data_emissao: e.target.value })}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="data_validade_inicial">Validade Inicial</Label>
          <Input
            id="data_validade_inicial"
            type="date"
            value={formData.data_validade_inicial}
            onChange={(e) => setFormData({ ...formData, data_validade_inicial: e.target.value })}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="data_validade_final">Validade Final *</Label>
          <Input
            id="data_validade_final"
            type="date"
            value={formData.data_validade_final}
            onChange={(e) => setFormData({ ...formData, data_validade_final: e.target.value })}
            required={formData.tipo_validade === "data_fixa"}
          />
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="modo_controle">Modo de Controle</Label>
          <Select
            value={formData.modo_controle || "manual_controlado"}
            onValueChange={(value) => setFormData({ ...formData, modo_controle: value })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Selecione o modo de controle" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="auto_renovavel">Auto renovável</SelectItem>
              <SelectItem value="manual_controlado">Manual controlado</SelectItem>
              <SelectItem value="somente_referencia">Somente referência</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <Label htmlFor="tipo_validade">Tipo de Validade</Label>
          <Select
            value={formData.tipo_validade || "data_fixa"}
            onValueChange={(value) => setFormData({ ...formData, tipo_validade: value })}
          >
            <SelectTrigger>
              <SelectValue placeholder="Selecione o tipo de validade" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="data_fixa">Data fixa</SelectItem>
              <SelectItem value="competencia">Competência (mês/ano)</SelectItem>
              <SelectItem value="evento">Por evento</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {formData.tipo_validade === "competencia" && (
          <div className="space-y-2">
            <Label htmlFor="competencia_mes_ano">Competência</Label>
            <Input
              id="competencia_mes_ano"
              type="month"
              value={formData.competencia_mes_ano || ""}
              onChange={(e) => setFormData({ ...formData, competencia_mes_ano: e.target.value })}
            />
          </div>
        )}
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="valor_declarado">Valor Declarado (R$)</Label>
          <Input
            id="valor_declarado"
            type="number"
            min="0"
            step="0.01"
            value={formData.valor_declarado ?? ""}
            onChange={(e) => setFormData({ ...formData, valor_declarado: e.target.value })}
            placeholder="Ex.: 250000.00"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="valor_minimo_exigido">Valor Mínimo Exigido (R$)</Label>
          <Input
            id="valor_minimo_exigido"
            type="number"
            min="0"
            step="0.01"
            value={formData.valor_minimo_exigido ?? ""}
            onChange={(e) => setFormData({ ...formData, valor_minimo_exigido: e.target.value })}
            placeholder="Ex.: 100000.00"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="link_solicitacao">Onde Solicitar a Certidão</Label>
        <Input
          id="link_solicitacao"
          type="url"
          value={formData.link_solicitacao}
          onChange={(e) => setFormData({ ...formData, link_solicitacao: e.target.value })}
          placeholder="https://exemplo.gov.br/solicitar-certidao"
        />
        <p className="text-xs text-slate-500">
          Insira o link do site onde a certidão pode ser solicitada
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="criterio_textual">Critério textual de conformidade</Label>
        <Textarea
          id="criterio_textual"
          value={formData.criterio_textual || ""}
          onChange={(e) => setFormData({ ...formData, criterio_textual: e.target.value })}
          placeholder="Ex.: Patrimônio líquido mínimo de 10% do valor estimado."
          rows={2}
        />
      </div>

      {isEdicao && (
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <Label>Renovação do Documento</Label>
            <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
              Novo
            </Badge>
          </div>
          <p className="text-sm text-slate-600">
            Ao adicionar um novo documento, o atual será movido para o histórico com sua data de validade.
          </p>
          
          <div className="border border-blue-200 rounded-lg p-4 bg-blue-50/50 space-y-3">
            <div className="space-y-2">
              <Label htmlFor="nova_data_validade">Data de Validade Final do Novo Documento *</Label>
              <Input
                id="nova_data_validade"
                type="date"
                value={novaDataValidade}
                onChange={(e) => setNovaDataValidade(e.target.value)}
                disabled={uploading || extractingContent}
                className="bg-white"
              />
            </div>

            <div className="space-y-2">
              <Label>Escolher Arquivo *</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="file"
                  onChange={handleUploadAnexo}
                  disabled={uploading || extractingContent}
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                  className="bg-white"
                />
                {(uploading || extractingContent) && (
                  <span className="text-sm text-slate-500">
                    {uploading ? 'Enviando...' : 'Extraindo texto...'}
                  </span>
                )}
              </div>
            </div>

            {extractingContent && (
              <p className="text-xs text-blue-700 font-medium">
                Extraindo conteúdo do documento para permitir busca por texto...
              </p>
            )}
          </div>
        </div>
      )}

      {isEdicao && formData.anexo_nome && (
        <div className="space-y-4">
          <div className="border border-green-200 rounded-lg p-3 bg-green-50">
            <p className="text-sm font-semibold text-green-900 mb-2">Documento Atual:</p>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-700 font-medium">{formData.anexo_nome}</p>
                <p className="text-xs text-green-600 mt-1">
                  Validade: {formData.data_validade_final ? new Date(formData.data_validade_final).toLocaleDateString('pt-BR') : '-'}
                </p>
              </div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => openUploadInNewTab(formData.anexo_url)}
              >
                Visualizar
              </Button>
            </div>
          </div>

          {formData.historico_anexos && formData.historico_anexos.length > 0 && (
            <div className="space-y-2">
              <Label>Documentos Anteriores ({formData.historico_anexos.length})</Label>
              <p className="text-xs text-slate-500">Histórico de documentos vencidos ou substituídos</p>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {formData.historico_anexos.map((anexo, index) => (
                  <div key={index} className="border border-slate-200 rounded-lg p-3 bg-slate-50 hover:bg-slate-100 transition-colors">
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">{anexo.nome}</p>
                        <div className="flex flex-col gap-1 mt-1">
                          <span className="text-xs text-slate-600">
                            Adicionado em: {new Date(anexo.data_adicao).toLocaleDateString('pt-BR')}
                          </span>
                          {anexo.data_validade_final && (
                            <span className="text-xs text-amber-700 font-medium">
                              Venceu em: {new Date(anexo.data_validade_final).toLocaleDateString('pt-BR')}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => downloadUploadFile(anexo.url, anexo.nome || 'documento')}
                        >
                          <Download className="w-4 h-4 mr-1" />
                          Baixar
                        </Button>
                        {user?.role === 'admin' && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => handleRemoveHistoricoAnexo(index)}
                            className="border-red-300 text-red-700 hover:bg-red-50"
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="space-y-2">
        <Label>E-mails para Notificação de Vencimento</Label>
        <div className="flex gap-2">
          <Input
            type="email"
            value={novoEmail}
            onChange={(e) => setNovoEmail(e.target.value)}
            placeholder="email@exemplo.com"
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleAddEmail();
              }
            }}
          />
          <Button type="button" onClick={handleAddEmail} variant="outline">
            <Plus className="w-4 h-4" />
          </Button>
        </div>
        {formData.emails_notificacao && formData.emails_notificacao.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {formData.emails_notificacao.map((email, index) => (
              <Badge key={index} variant="secondary" className="flex items-center gap-2">
                {email}
                <button
                  type="button"
                  onClick={() => handleRemoveEmail(email)}
                  className="hover:text-red-600"
                >
                  <X className="w-3 h-3" />
                </button>
              </Badge>
            ))}
          </div>
        )}
        <p className="text-xs text-slate-500">
          Com vencimento entre <strong>hoje</strong> e os <strong>próximos 10 dias</strong>, enviamos
          <strong> um e-mail por documento</strong> (uma vez), ao abrir a página Gestão de Documentos.
          É necessário <strong>SMTP configurado</strong> no servidor.
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="observacoes">Observações</Label>
        <Textarea
          id="observacoes"
          value={formData.observacoes}
          onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
          placeholder="Observações adicionais sobre o documento..."
          rows={3}
        />
      </div>

      {!isEdicao && (
        <div className="space-y-2">
          <Label htmlFor="upload_documento">Upload do Documento</Label>
          <div className="flex items-center gap-2">
            <Input
              id="upload_documento"
              type="file"
              onChange={handleUploadAnexoInicial}
              disabled={uploadingInicial || extractingContent}
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
            />
            {(uploadingInicial || extractingContent) && (
              <span className="text-sm text-slate-500">
                {uploadingInicial ? 'Enviando...' : 'Extraindo texto...'}
              </span>
            )}
          </div>
          {formData.anexo_nome && (
            <div className="border border-green-200 rounded-lg p-3 bg-green-50 mt-2">
              <p className="text-sm font-medium text-green-700">{formData.anexo_nome}</p>
            </div>
          )}
        </div>
      )}

      <div className="flex justify-end gap-3 pt-4 border-t">
        <Button type="button" variant="outline" onClick={onCancelar} disabled={isSaving}>
          Cancelar
        </Button>
        <Button type="submit" disabled={isSaving} className="bg-blue-600 hover:bg-blue-700">
          {isSaving ? 'Salvando...' : 'Salvar Documento'}
        </Button>
      </div>
    </form>
  );
}